import Head from 'next/head';
import Image from 'next/image';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { setItem } from '../../lib/localStorage';

import {
  HeroLanding,
  CoursesOverview,
  CoursesReview,
  CoursesInfoCard,
  CourseExperts,
  CourseFaq,
  CoursePackagesOverview,
  CourseBundleCard,
  CoursesInsuranceBanner
} from '@components';
import Preloader from '../../shared/preloader';

const courses = [
  {
    title: 'Zwangerschapscursus',
    imageSrc: require('@assets/images/courses/zwangerschapscursus.webp'),
    list: [
      'Leer alles over je zwangerschap',
      'Ga met meer vertrouwen je bevalling tegemoet',
      'Weet wat je te wachten staat',
      'In samenwerking met verloskundigen en echoscopisten'
    ],
    link: '/zwangerschapscursus/zwangerschapscursus/introductievideo',
    imageAlt: 'Zwangere vrouw doet zwangerschapscursus thuis op de bank'
  },
  {
    title: 'Bevallingscursus',
    imageSrc: require('@assets/images/courses/bevallingscursus.webp'),
    list: [
      'Weet wat je kan verwachten tijdens je bevalling',
      'Leer alles over de verschillende fases, pijnbestrijding, weeën én het gouden uur',
      'Ga met meer vertrouwen je bevalling tegemoet',
      'Gegeven door verloskundigen'
    ],
    link: '/zwangerschapscursus/bevallingscursus/introductievideobevalingscursus',
    imageAlt:
      'Net bevallen vrouw heeft is blij dat ze een bevallingscursus heeft gedaan'
  },
  {
    title: 'Borstvoedingscursus',
    imageSrc: require('@assets/images/courses/borstvoedingscursus.webp'),
    list: [
      'Leer alles over voedingssignalen, houdingen en voeden op verzoek',
      'Ontdek veelvoorkomende problemen en hoe hiermee om te gaan',
      'Stel je vragen in de hechte community',
      'Gegeven door een lactatiekundige'
    ],
    link: '/zwangerschapscursus/borstvoedingscursus/introductievideo',
    imageAlt:
      'Baby drinkt uit de borst nadat moeder borstvoedingscursus heeft gedaan'
  },
  {
    title: 'Zwangerschapsyoga',
    imageSrc: require('@assets/images/courses/zwangerschapsyoga.webp'),
    list: [
      'Yoga-oefeningen en ademhalingstechnieken voor de zwangerschap en bevalling',
      'Volgens de Birthlight methode',
      'Inclusief een hele les zwangerschapsyoga',
      'Gegeven door een docent zwangerschapsyoga'
    ],
    link: '/zwangerschapscursus/zwangerschapsyoga/introductievideo',
    imageAlt: 'Zwangere vrouw doet thuis een cursus zwangerschapsyoga'
  },
  {
    title: 'Voeding zwangerschap',
    imageSrc: require('@assets/images/courses/voeding-tijdens-zwangerschap.webp'),
    list: [
      'Ontdek wat je wel en beter niet kan eten',
      'Leer hoe je met voeding kwaaltjes kan voorkomen of verminderen',
      'Bereid je voor op de eerste hapjes van je baby',
      'Gegeven door een diëtiste'
    ],
    link: '/zwangerschapscursus/voedingscursus/introductievideo',
    imageAlt:
      'Zwangere vrouw en partner koken gezond na cursus voeding tijdens je zwangerschap'
  },
  {
    title: 'Hypnobirthing',
    imageSrc: require('@assets/images/courses/hypnobirth.webp'),
    list: [
      'Bereid je voor op een positieve bevalling',
      'Leer verschillende technieken om te ontspannen',
      'Inclusief oefeningen met je partner',
      'Gegeven door de voorzitter van de HypnoBirthing Vereniging'
    ],
    link: '/zwangerschapscursus/hypnobirthing/introductievideo',
    imageAlt:
      'Partner ondersteunt vrouw bij weeën nadat zij de cursus hypnobirthing deden'
  },
  {
    title: 'Sporten en bewegen',
    imageSrc: require('@assets/images/courses/sporten-en-bewegen-tijdens-zwangerschap.webp'),
    list: [
      'Voorkom en verhelp zwangerschaps-gerelateerde klachten',
      'Leer welke oefeningen je wel en beter niet kan doen',
      'Inclusief oefeningen voor thuis',
      'Gegeven door een fysiotherapeut'
    ],
    link: '/zwangerschapscursus/sporten-en-bewegen/introductievideo',
    imageAlt: 'Zwangere vrouw doet cursus sporten en bewegen thuis'
  },
  {
    title: 'Seksualiteit',
    imageSrc: require('@assets/images/courses/seksualiteit-tijdens-en-na-de-zwangerschap.webp'),
    list: [
      'Krijg inzicht in veranderingen die invloed hebben op seksualiteit',
      'Leer hoe je hier samen met je partner mee omgaat',
      'Krijg meer begrip en verbinding binnen jullie relatie',
      'Gegeven door een consulent Seksuele Gezondheid'
    ],
    link: '/zwangerschapscursus/seksualiteit-tijdens-en-na-de-zwangerschap/introductievideo',
    imageAlt:
      'Zwangere vrouw en haar partner doen thuis samen de cursus seksualiteit tijdens en na de zwangerschap'
  },
  {
    title: 'Mindfull zwanger',
    imageSrc: require('@assets/images/courses/mindfulness-voor-jonge-ouders.webp'),
    list: [
      'Verminder stress tijdens je zwangerschap',
      'Leer omgaan met lastige momenten en gevoelens',
      'Inclusief meditaties en visualisaties',
      'Gegeven door een meditatiedocente'
    ],
    link: '/zwangerschapscursus/mindfulnesscursus/introductievideo',
    imageAlt: 'Zwangere vrouw doet thuis op bed de audiocursus mindful zwanger'
  },
  {
    title: 'Dragen en hechting',
    imageSrc: require('@assets/images/courses/dragen-en-hechting.webp'),
    list: [
      'Leer alles over hechting en het dragen van je baby',
      'Ontdek de verschillende draagtechnieken',
      'Leer van de verhalen van andere ouders',
      'Gegeven door een hechtingscoach en draagconsulent'
    ],
    link: '/zwangerschapscursus/dragen-en-hechting/introductievideo',
    imageAlt:
      'Vrouw draagt haar baby in de draagzak nadat zij de cursus dragen en hechting volgde'
  },
  {
    title: 'Babymassage',
    imageSrc: require('@assets/images/courses/babymassage.webp'),
    list: [
      'Leer hoe je jouw baby masseert',
      'Ontdek handige trucs om blessures te voorkomen',
      'Voor ultieme ontspanning voor jou en je baby',
      'Gegeven door een docent babymassage'
    ],
    link: '/zwangerschapscursus/babymassage/introductievideo',
    imageAlt: 'Baby krijgt babymassage'
  },
  {
    title: 'Slaapcursus 0-6 mnd',
    imageSrc: require('@assets/images/courses/slaapcursus-0-6-maanden.webp'),
    list: [
      'Leer alles over het slaappatroon van je baby',
      'Krijg meer rust rondom de slaapjes van je baby',
      'Creëer een vast slaapritueel',
      'Volgens de Gentle Sleep methode'
    ],
    link: '/zwangerschapscursus/slaapcursus0-6maanden/introductievideo',
    imageAlt: 'Gapende baby gaat heerlijk slapen door een slaapcursus'
  },
  {
    title: 'Slaapcursus 0,5 – 5 jaar',
    imageSrc: require('@assets/images/courses/slaapcursus-kind.webp'),
    list: [
      'Leer hoe je de slaap van je kindje verbetert',
      'Ontdek alles over slaapgewoonten, rituelen en -associaties.',
      'Volgens de Gentle Sleep methode',
      'Gegeven door slaapcoaches'
    ],
    link: '/zwangerschapscursus/slaapcursus-pakket/introductievideo',
    imageAlt: 'Kind slaapt goed na het volgen van een slaapcursus'
  },
  {
    title: 'De eerste 100 dagen',
    imageSrc: require('@assets/images/courses/eerste-100-dagen.webp'),
    list: [
      'Leer alles over de eerste 100 dagen met je baby',
      'Weet wat je te wachten staat en wat je kan doen',
      'Weet waar je hulp kan krijgen',
      'Gegeven door zorgprofessionals'
    ],
    link: '/zwangerschapscursus/de-eerste-100-dagen-na-de-bevalling/introductievideo',
    imageAlt: 'Pasgeboren baby kijkt moeder liefdevol aan'
  },
  {
    title: 'Babygebaren',
    imageSrc: require('@assets/images/courses/baby-en-kindergebaren.webp'),
    list: [
      'Ontdekt gebaren als communicatie met je baby',
      'Pas de gebaren direct toe',
      'Leer spelenderwijs gebaren inzetten',
      'Gegeven door een docent kindergebaren'
    ],
    link: '/zwangerschapscursus/babysignalen-en-baby-en-kinder-gebaren/introductievideo',
    imageAlt: 'Moeder en baby oefenen babysignalen en -gebaren'
  },
  {
    title: 'Mindful ouderschap',
    imageSrc: require('@assets/images/courses/mindfulness-voor-jonge-ouders.webp'),
    list: [
      'Verminder stress',
      'Leer omgaan met lastige momenten en gevoelens',
      'Inclusief meditaties en visualisaties',
      'Gegeven door een meditatiedocente'
    ],
    link: '/zwangerschapscursus/mindfulness-na-bevalling/introductievideo',
    imageAlt: 'Vrouw volgt thuis een cursus mindfulness na de bevalling'
  },
  {
    title: 'Herstel na de bevalling',
    imageSrc: require('@assets/images/courses/herstel-na-de-bevalling.webp'),
    list: [
      'Jouw gids voor het moederschap',
      'Doorgrond je hormonen',
      'Omarm je nieuwe lichaam',
      'Gegeven door een gezondheids-wetenschapper'
    ],
    link: '/zwangerschapscursus/herstel-na-de-bevalling-sep/introductievideo',
    imageAlt: 'Vrouw volgt thuis de cursus herstel na de bevalling'
  }
];

const experts = [
  {
    avatar: require('@assets/images/courses/logo-deskundigen-zwangerschapscursus.webp'),
    name: '040verloskunde',
    role: 'Verloskundigen & echoscopisten',
    description:
      'Verschillende verloskundigen van Verloskundigenpraktijk 040verloskunde vertellen je alles over je zwangerschap. Door hun jarenlange ervaring weten zij precies waar jij tegenaan loopt en begeleiden jou van A tot Z naar je bevalling. <br><a target="_blank" rel="noopener noreferrer" href="https://www.040verloskunde.nl/over-ons/team/">Bekijk het team van 040 ›</a>'
  },
  {
    avatar: require('@assets/images/courses/verloskundige-fieke.webp'),
    name: 'Fieke van Rijswijk',
    role: 'Verloskundige en versiekundige',
    description:
      'Fieke is al jaren verloskundige en krijgt energie van een mooie bevalling. Ze is gespecialiseerd in uitwendige versies, waarbij een baby in de buik wordt gedraaid. Fieke werkt bij verloskundigenpraktijk Eva van Hoorn.'
  },
  {
    avatar: require('@assets/images/courses/susanne-jumelet.webp'),
    name: 'Susanne Jumelet',
    role: 'Verloskundige en lactatiekundige',
    description:
      'Susanne is al ruim 20 jaar verloskundige. Ze houdt ervan om midden in de nacht op pad te gaan om een barende en partner te ondersteunen. Susanne werkt sinds 2007 bij verloskundigenpraktijk Eva van Hoorn.'
  },
  {
    avatar: require('@assets/images/courses/lactatiekundige-annette-luime.webp'),
    name: 'Annette Luime',
    role: 'Lactatiekundige',
    description:
      'Annette is al jarenlang lactatiekundige IBCLC en is aangesloten bij de NVL (Nederlandse Vereniging van Lactatiekundigen). Ze werkt op basis van haptonomie en gebruikt dit in haar werk met ouders en baby’s.'
  },
  {
    avatar: require('@assets/images/courses/zwangerschapsyoga-docent-adrienne-oudenampse.webp'),
    name: 'Adrienne Oudenampsen',
    role: 'Docent zwangerschapsyoga',
    description:
      'Adrienne heeft jarenlang ervaring als yoga-docent. Ze vindt rust in het ritme van het ademen en kracht in de oefeningen van de asana’s. Adrienne werkt bij Rocket Yoga.'
  },
  {
    avatar: require('@assets/images/courses/marielle-voedingsdietiste.webp'),
    name: 'Mariëlle Mullier',
    role: 'Diëtiste',
    description:
      'Mariëlle is al ruim 20 jaar diëtiste en heeft haar eigen praktijk Vodicare. Ze geniet ervan om mensen te motiveren om gezonder te eten en leven. <br><a target="_blank" rel="noopener noreferrer" href="www.vodicare.nl">Bekijk meer van Vodicare ></a>'
  },
  {
    avatar: require('@assets/images/courses/petra-lomartire.webp'),
    name: 'Petra Lomartire',
    role: 'Docente HypnoBirthing',
    description:
      'Petra is voorzitter van de HypnoBirthing Vereniging Nederland & België. Ze werkt al sinds jaartal als docent HypnoBirthing en kan hier geen genoeg van krijgen. Meer weten? <br><a target="_blank" rel="noopener noreferrer" href="http://www.hypnobirthingnederland.nl/">Bekijk de website van hypnobirthing > </a>'
  },
  {
    avatar: require('@assets/images/courses/annelieke-janssen.webp'),
    name: 'Annelieke Janssen',
    role: 'Fysiotherapeut en trainer',
    description:
      'Annelieke is fysiotherapeut met een eigen trainingsprogramma voor zwangeren of de periode na de bevalling. Sinds 2015 heeft ze haar eigen praktijk. <br><a target="_blank" rel="noopener noreferrer" href="https://www.anneliekejanssen.nl/">Lees mee over Annelieke ></a>'
  },
  {
    avatar: require('@assets/images/courses/verloskundige-dieuwke-ottens.webp'),
    name: 'Dieuwke Ottens',
    role: 'Verloskundige & Consulent seksuele gezondheid',
    description:
      'Dieuwke is sinds 2001 verloskundige en sinds 2015 ook als Consulent Seksuele Gezondheid. Ze is gespecialiseerd in de koppeling tussen verloskunde en seksuologie. Hierdoor weet zij alles over problemen rondom seksualiteit tijdens en na de zwangerschap.'
  },
  {
    avatar: require('@assets/images/courses/anne-van-leeuwen.webp'),
    name: 'Anne van Leeuwen',
    role: 'Meditatiedocent',
    description:
      'Anne is al jarenlang docent meditatie en heeft haar eigen praktijk ‘Yoga geeft energie’. Daar verzorgt zij onder andere yogalessen en meditatiesessies. Ook organiseert zij yogaweekenden. <br><a target="_blank" rel="noopener noreferrer" href="https://www.yogageeftenergie.nl/">Lees meer over Yoga Geeft Energie ></a>'
  },
  {
    avatar: require('@assets/images/courses/rachel.webp'),
    name: 'Rachel',
    role: 'Hechtingscoach & draagdoekconsulent',
    description:
      'Rachel begeleidt in haar eigen praktijk Hechte band jonge ouders naar een goede hechting. Ze ziet het als haar missie om jonge ouders het vertrouwen te geven in hun opvoed-wijsheid. <br><a target="_blank" rel="noopener noreferrer" href="https://www.hechteband.nl/">Bekijk meer van Hechte Band ></a>'
  },
  {
    avatar: require('@assets/images/courses/manja-babymassage.webp'),
    name: 'Manja Bos',
    role: 'Docent babymassage',
    description:
      'Manja werkt sinds 2018 als docent babymassage. Ze heeft haar eigen praktijk Bij Spruit, waar je terecht kan voor babymassage, dreumes-peuterdans en meer. <br><a target="_blank" rel="noopener noreferrer" href="https://www.bijspruit.nl/">Bekijk meer van Bij Spruit></a>'
  },
  {
    avatar: require('@assets/images/courses/karolien-slaapcoach.webp'),
    name: 'Karolien Kop-Huisman',
    role: 'Gentle Sleep Coach®',
    description:
      'Karolien is logopedist en slaapcoach. Samen met Geertje heeft ze haar eigen By Sleep. Het is haar passie om (oververmoeide) gezinnen te helpen met het oplossen van hun slaapproblemen en daarmee hun energie terug te geven.'
  },
  {
    avatar: require('@assets/images/courses/geertje-van-heeshwijk-slaapcoach.webp'),
    name: 'Geertje van Heeswijk',
    role: 'Gentle Sleep Coach®',
    description:
      'Geertje is slaapcoach en heeft samen met Karolien haar eigen By Sleep. Het terugbrengen van een beetje meer slaap ziet zij als het meest waardevolle wat ze een gezin kan bieden.'
  },
  {
    avatar: require('@assets/images/courses/ineke-de-kruijff-kinderarts.webp'),
    name: 'Ineke de Kruijff',
    role: 'Kinderarts',
    description:
      'Ineke is kinderarts in het St. Antonius Ziekenhuis in Utrecht. In 2022 promoveerde zij op het onderwerp ‘excessive infant crying’. Ze neemt ouders met een huilbaby zeer serieus en zet zich in om hier informatie over te verspreiden.'
  },
  {
    avatar: require('@assets/images/courses/karola-de-graaf-kinderarts.webp'),
    name: 'Karola de Graaf',
    role: 'Kinderarts (i.o.)',
    description:
      'Karola is kinderarts in opleiding bij het Wilhelmina Kinderziekenhuis. Ook doet ze onderzoek naar huilbaby’s en kijkt of een nieuwe werkwijze tussen jeugdgezondheidszorg, huisarts en kinderarts kan zorgen voor minder ziekenhuisopnames.'
  },
  {
    avatar: require('@assets/images/courses/marga-klessens.webp'),
    name: 'Marga Klessens',
    role: 'Preverbaal logopedist',
    description:
      'Marga is al meer dan 30 jaar werkzaam als preverbaal logopedist. Ze behandelt baby’s en jonge kinderen (0-3 jaar) bij de ouders thuis. Ook is zij preverbaal logopedist in het Antonius Ziekenhuis in Nieuwegein.'
  },
  {
    avatar: require('@assets/images/courses/elsbeth-van-voort-kinderfysio.webp'),
    name: 'Elsbeth van der Voort',
    role: 'Kinderfysio',
    description:
      'Elsbeth is kinderfysio bij Jan van Rijn Kinderfysiotherapie. Ze verwondert zich dat ieder kind zich op zijn eigen manier ontwikkelt. Ze voelt zich bevoorrecht de ouders en het kind te begeleiden naar een optimale motorische ontwikkeling.'
  },
  {
    avatar: require('@assets/images/courses/marie-louise-van-doorn-jeugdverpleegkundige.webp'),
    name: 'Marie-Louise van Doorn',
    role: 'Jeugdverpleegkundige',
    description:
      'Marie-Louise is jeugdverpleegkundige in Utrecht. Daarnaast is zij verpleegkundige Maatschappij en Gezondheid.'
  },
  {
    avatar: require('@assets/images/courses/karin-sprenkelder-jeugdarts.webp'),
    name: 'Karin Sprenkelder',
    role: 'Jeugdarts',
    description:
      'Karin is Jeugdarts bij het consultatiebureau. Samen met andere professionals zet zij zich in om de zorg rondom huilbaby’s en hun ouders te verbeteren.'
  },
  {
    avatar: require('@assets/images/courses/andre-de-vries-huisarts.webp'),
    name: 'André de Vries',
    role: 'Huisarts',
    description:
      'André is huisarts. Ook is hij kaderhuisarts bewegingsapparaat en als docent verbonden aan de huisartsopleiding in Amsterdam (AMC).'
  },
  {
    avatar: require('@assets/images/courses/judith-wahl.webp'),
    name: 'Judith Wahl',
    role: 'Medisch Pedagogisch Zorgverlener',
    description:
      'Judith is medisch pedagogisch zorgverlener in het Antonius Ziekenhuis in Nieuwegein. Ze zorgt ervoor dat een ziekenhuisbezoek of opname zo prettig mogelijk verloopt, en ondersteunt de kinderen en hun ouders bij de vragen die ze hebben.'
  },
  {
    avatar: require('@assets/images/courses/kjille-soeting.webp'),
    name: 'Kjille Soeting',
    role: 'Baby- en kindergebaren docent',
    description:
      'Kjille werkt als 20 jaar in de kinderopvang als pedagogisch medewerker en al sinds 2010 als docent baby- en kindergebaren. Zij kan je daarom alles uitleggen over de methode en het leren van gebaren.'
  },
  {
    avatar: require('@assets/images/courses/lissa-zeviar.webp'),
    name: 'Lissa Zeviar',
    role: 'Oprichter gebarenstem',
    description:
      'Lissa is de oprichter van Gebarenstem. Zij is moeder van een doof kindje en weet daarom als geen ander het belang van gebarentaal. Het is haar missie om iedereen hiermee kennis te laten maken.'
  },
  {
    avatar: require('@assets/images/courses/desiree.webp'),
    name: 'Desirée Domacassé',
    role: 'Gezondheidswetenschapper (MSc)',
    description:
      'Desiree is en gezondheidswetenschapper (MSc) met een achtergrond in biomedische wetenschappen, geregistreerd bij Natuur Diëtisten Nederland als therapeur en gespecialiseerd in het helpen van moeders. Ze heeft haar eigen bedrijf <br><a target="_blank" rel="noopener noreferrer" href="https://daisysyellowpepper.nl/">Daisy’s Yellow Pepper ></a>'
  }
];

const faqItems = [
  {
    question: 'Waarom een online zwangerschapscursus?',
    answer:
      'Een online zwangerschapscursus is handig als jij je goed wil voorbereiden op de veranderingen in je lichaam, de bevalling en het ouderschap, gewoon vanuit huis. Je hebt toegang tot betrouwbare informatie en kan de cursus volgen waar en wanneer het jou uitkomt. Zo leer je alles over de zwangerschap, de bevalling en wat je daarna kunt verwachten. Met praktische tips, betrouwbare informatie en handige video’s voel je je zekerder en beter voorbereid. Een zwangerschapscursus online is dus niet alleen makkelijk, maar ook een fijne manier om je voor te bereiden op de komst van je baby.'
  },
  {
    question: 'Wanneer kan ik het beste starten met een zwangerschapscursus?',
    answer:
      'Het beste moment om te starten met een zwangerschapscursus is tussen de 20 en 24 weken van je zwangerschap. Dan heb je genoeg tijd om alles rustig door te nemen en ben je nog niet te dicht bij de bevalling. Je kan de cursus ook later beginnen, maar hoe eerder je start, hoe meer tijd je hebt om je voor te bereiden op de bevalling en het ouderschap.'
  },
  {
    question: 'Welke cursuspakketten bieden jullie aan?',
    answer:
      'Cursusportaal heeft verschillende pakketten. Zo kan je zelf kiezen welk pakket het beste bij jou past.<br><ul><li><strong>Het basispakket</strong> bestaat uit 8 cursussen die je ondersteunen tijdens je zwangerschap en de bevalling. Dit pakket kost nu €99,95 i.p.v. €149,95. Mogelijk vergoed je aanvullende verzekering dit pakket (deels).</li><li><strong>Het pluspakket</strong> bevat 17 cursussen die je ondersteunen tijdens je zwangerschap, de bevalling en het ouderschap. Dit pakket kost nu €139,95 i.p.v. €249,95.</li><li><strong>Het pro-pakket</strong> bevat dezelfde 17 cursussen als het pluspakket, maar je hebt extra lang toegang tot de cursussen. Dit pakket kost nu €179,95 i.p.v. €299,95.</li></ul>'
  },
  {
    question: 'Welke cursussen zitten in het basispakket?',
    answer:
      'In het basispakket vind je de volgende 8 zwangerschapscursussen:<br><ul><li>Zwangerschapscursus</li><li>Bevallingscursus</li><li>Borstvoedingscursus</li><li>Zwangerschapsyoga</li><li>Voeding</li><li>Introductie hypnobirthing</li><li>Sporten en bewegen</li><li>Mindfulness zwangerschap</li></ul>'
  },
  {
    question: 'Welke cursussen zitten in het pluspakket?',
    answer:
      'In het pluspakket vind je de volgende 17 zwangerschaps- en ouderschapscursussen:<br><ul><li>Zwangerschapscursus</li><li>Bevallingscursus</li><li>Borstvoedingscursus</li><li>Zwangerschapsyoga</li><li>Voeding</li><li>Introductie hypnobirthing</li><li>Sporten en bewegen</li><li>Mindfulness zwangerschap</li><li>Herstel na de bevalling</li><li>Dragen en hechting</li><li>Babymassage</li><li>Slaapcursus 0 – 6 maanden</li><li>Slaapcursus 6 maanden – 5 jaar</li><li>Seksualiteit tijdens en na de zwangerschap</li><li>De eerste 100 dagen na de bevalling</li><li>Babysignalen en gebaren</li><li>Mindfullness na de bevalling</li></ul>'
  },
  {
    question: 'Welke cursussen zitten in het pro-pakket?',
    answer:
      'In het pro-pakket vind je de volgende 17 zwangerschaps- en ouderschapscursussen:<br><ul><li>Zwangerschapscursus</li><li>Bevallingscursus</li><li>Borstvoedingscursus</li><li>Zwangerschapsyoga</li><li>Voeding</li><li>Introductie hypnobirthing</li><li>Sporten en bewegen</li><li>Mindfulness zwangerschap</li><li>Herstel na de bevalling</li><li>Dragen en hechting</li><li>Babymassage</li><li>Slaapcursus 0 – 6 maanden</li><li>Slaapcursus 6 maanden – 5 jaar</li><li>Seksualiteit tijdens en na de zwangerschap</li><li>De eerste 100 dagen na de bevalling</li><li>Babysignalen en gebaren</li><li>Mindfullness na de bevalling</li></ul>'
  },
  {
    question: 'Hoelang heb ik toegang tot de cursussen?',
    answer:
      'Hoelang je toegang hebt tot de cursussen, hangt af van het pakket dat je hebt.<br><ul><li><strong>Basispakket:</strong> je hebt 12 maanden toegang tot 8 zwangerschapscursussen.</li><li><strong>Pluspakket:</strong> je hebt 18 maanden toegang tot alle 17 zwangerschaps- en ouderschapscursussen.</li><li><strong>Pro-pakket:</strong> je hebt 36 maanden toegang tot alle 17 zwangerschaps- en ouderschapscursussen.</li></ul>'
  },
  {
    question: 'Hoe gaat het na aankoop?',
    answer:
      'Na het kopen van je cursuspakket ontvang je een e-mail met een unieke code. Met deze code krijg je toegang tot alle cursussen in je persoonlijke cursusportaal. Heb je nog geen account bij zwangerenportaal? Maak dan eerst een account aan. Zodra je bent ingelogd, vind je de cursussen in je persoonlijke omgeving.'
  },
  {
    question: 'Krijg ik jullie cursussen vergoed door mijn zorgverzekeraar?',
    answer:
      "Veel zorgverzekeraars vergoeden (een deel van) de kosten van onze zwangerschapscursussen. Dit betekent dat je onze cursussen soms helemaal gratis kan volgen, of dat je een deel van het bedrag vergoed krijgt.<br><a href='/verzekering/cursus-vergoeding' target='_blank'>Kijk hier</a> of jouw zorgverzekeraar de cursussen vergoedt en hoeveel je terugkrijgt."
  },
  {
    question: 'Hoe kan ik de cursus vergoed krijgen?',
    answer:
      'Veel zorgverzekeraars vergoeden (deels) de kosten van een zwangerschapscursus als je een aanvullende verzekering hebt. Zo kan je soms de cursussen gratis volgen of een deel van het bedrag terugkrijgen. Zo werkt het:<br><ol><li><strong>Bekijk je zorgverzekering:</strong> Check of zwangerschapscursussen vergoed worden in je aanvullende verzekering.</li><li><strong>Vraag een factuur aan:</strong> Na aankoop van het cursuspakket ontvang je een mail waarin je een factuur kan aanvragen bij ons. Vul alle gegevens in die wij nodig hebben voor de factuur. Deze kan je indienen bij je zorgverzekeraar. Let op: je kan de factuur tot 24 uur na aankoop aanvragen. Daarna vervalt deze mogelijkheid.</li><li><strong>Dien de factuur in:</strong> Stuur de factuur naar je zorgverzekeraar, bijvoorbeeld via hun website of app.</li><li><strong>Let op het vergoedingsbedrag:</strong> De hoogte van de vergoeding verschilt per zorgverzekeraar en pakket, dus kijk goed hoeveel je terug kan krijgen.</li></ol>'
  },
  {
    question: 'Waar vind ik de factuur voor mijn zorgverzekeraar?',
    answer:
      'Je kan de factuur voor je zorgverzekeraar terugvinden in jouw cursusportaal. Log in bij zwangerenportaal of maak een account aan, ga naar jouw cursusportaal en klik op ‘factuur’. De factuur is als PDF te downloaden. Let op: vraag de factuur binnen 24 uur na aankoop van het cursuspakket aan. Daarna vervalt de mogelijkheid om een factuur aan te vragen.'
  },
  {
    question: 'Waarom willen jullie mijn adresgegevens weten?',
    answer:
      'Deze gegevens hebben wij nodig om een factuur op te maken voor jouw zorgverzekeraar. Vergoed jouw zorgverzekeraar onze cursussen? Dan wil je zorgverzekeraar de volgende gegevens weten:<br><ul><li>Je voornaam en achternaam</li><li>Je e-mailadres</li><li>Je adres</li><li>Je geboortedatum</li></ul>'
  },
  {
    question: 'Over cursusportaal',
    answer:
      'Cursusportaal is een samenwerking tussen zwangerenportaal en 24baby. Beide platforms staan voor betrouwbare informatie en het ondersteunen van (aanstaande) ouders in hun reis naar het ouderschap. De zwangerschapscursussen van cursusportaal zijn allemaal gemaakt in samenwerking met deskundigen. Ze zijn daarom een goede voorbereiding op de zwangerschap, bevalling en het ouderschap, met betrouwbare informatie van deskundigen op ieder gebied.'
  }
];

export default function Page({ user, userDataLoading, addUserEvent }) {
  const router = useRouter();

  useEffect(() => {
    addUserEvent({
      variables: { type: 'courses-landingspage', data: '{}' }
    });

    setItem('coursesLandingUrl', router.asPath);

    if (user.hasCourseAccess) router.push('/zwangerschapscursus/ouder-en-kind');
  }, [user.hasCourseAccess]);

  if (userDataLoading) return <Preloader />;

  return (
    <>
      <Head>
        <title>Zwangerschapscursus</title>
      </Head>

      <HeroLanding
        title="Ontdek onze zwangerschaps-cursussen"
        content={() =>
          'Volg je zwangerschapscursus thuis, in je eigen tempo. Zo groei je vol vertrouwen naar het ouderschap: van eerste schopjes tot eerste ontmoeting.'
        }
        buttonLink="#cursuspakketten"
        buttonLabel="Bekijk cursuspakketten"
        imageSrc={require('@assets/images/courses/ontdek-zwangerschapscursussen.webp')}
        imageAlt="Zwangere vrouw en partner doen een online zwangerschapscursus bij cursusportaal"
        courseCount={17}
      />

      <CoursesOverview
        courses={courses}
        title="Alle zwangerschapscursussen"
        subtitle="Deze 17 cursussen vind je bij cursusportaal"
      />

      <CoursePackagesOverview />

      <CourseBundleCard.Benefits
        benefits={[
          'Inzichten van experts',
          '12 tot 36 maanden onbeperkt toegang',
          'Meerdere cursussen voor één prijs',
          'Te volgen waar en wanneer je wil'
        ]}
      />

      <CoursesInfoCard
        image={
          <Image
            src={require('@assets/images/courses/leer-van-experts.webp')}
            alt="Jonge vrouw doet een zwangerschapscursus thuis op de bank met haar vriend"
            sizes="(min-width: 1200px) 60vw, 100vw"
            placeholder="blur"
          />
        }
        content={
          <>
            <h2>
              Leer van onze experts,
              <br />
              wanneer het jou uitkomt
            </h2>
            <p>
              De cursussen van cursusportaal volg je waar en wanneer het jou
              uitkomt. Log in op je laptop, tablet of telefoon en volg de
              cursussen overal waar je wil.
            </p>
          </>
        }
      />

      <CoursesInsuranceBanner />
      <CoursesReview
        trackingCategory="welkom-lander"
        trackingAction="click-start-jouw-cursus-nu"
        buttonLink="#cursuspakketten"
      />

      <CoursesInfoCard
        image={
          <Image
            src={require('@assets/images/courses/zwangerschapspakketten-zwangerschapscursussen.webp')}
            alt="Lachende zwangere vrouw en haar partner volgen samen een zwangerschapscursus op hun telefoon, thuis op de bank"
            placeholder="blur"
          />
        }
        content={
          <>
            <h2>Groei vol vertrouwen naar het ouderschap</h2>
            <p>
              Cursusportaal heeft verschillende pakketten met cursussen. De
              cursussen bereiden je voor op je zwangerschap, de bevalling en het
              ouderschap. Zo weet je beter wat je kan verwachten in elke fase,
              en groei jij met meer vertrouwen naar het ouderschap.
            </p>
            <p>
              Wil jij alleen de babycursussen volgen? Bekijk dan onze{' '}
              <a href="/zwangerschapscursus/ouder-en-kind">babypakketten.</a>
            </p>
          </>
        }
        reverse
      />

      <CourseExperts experts={experts} />
      <CourseFaq items={faqItems} />
    </>
  );
}
